"use client";

/**
 * FileDropGuard — soltar um arquivo FORA de uma área de anexo não pode mais
 * levar o app embora.
 *
 * Comportamento padrão do browser: arquivo solto em qualquer lugar da página
 * vira navegação (`file:///…` ou o blob aberto na aba). No PWA/TWA instalado
 * isso é caro — o mestre erra o alvo por 30px ao anexar uma foto e perde a
 * tela inteira, com o rascunho que estava digitando junto. É o tipo de erro
 * que a pessoa comete exatamente quando está com pressa.
 *
 * A regra é `preventDefault` nos DOIS eventos: só no `drop` não basta, porque
 * sem o `dragover` o browser nem considera a janela um alvo válido.
 *
 * Como NÃO atrapalha as áreas de anexo reais (Composer do chat, anexos de
 * tarefa/compra/contrato, Drive da obra): os handlers do React rodam no
 * container raiz, ANTES deste listener na window. Quem já assumiu o arraste
 * chega aqui com `defaultPrevented`, e o guard só deixa passar — o
 * `dropEffect = "none"` (cursor de "não pode") fica pro resto da tela.
 *
 * Escopo deliberadamente estreito, pra não quebrar o que já funciona:
 *  - só arraste de ARQUIVO (`types` com "Files"); arrastar texto selecionado
 *    pra dentro de um campo continua igual;
 *  - `<input type="file">` nativo é pulado — ele aceita drop pelo padrão do
 *    browser, e bloquear ali quebraria o anexo em vez de proteger;
 *  - kanbans (dnd-kit) usam pointer events, não HTML5 drag — nem passam aqui.
 */

import { useEffect } from "react";

export default function FileDropGuard() {
  useEffect(() => {
    const isFileDrag = (e: DragEvent) =>
      e.dataTransfer?.types?.includes("Files") ?? false;

    const isNativeFileInput = (target: EventTarget | null) =>
      target instanceof HTMLInputElement && target.type === "file";

    const onDragOver = (e: DragEvent) => {
      if (!isFileDrag(e) || isNativeFileInput(e.target)) return;
      // Uma área de anexo já reivindicou este arraste — não mexer no cursor
      // dela (ela mesma define "copy").
      if (e.defaultPrevented) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
    };

    const onDrop = (e: DragEvent) => {
      if (!isFileDrag(e) || isNativeFileInput(e.target)) return;
      // Sempre — é isto que segura a navegação. Se uma área de anexo já tratou,
      // repetir é no-op.
      e.preventDefault();
    };

    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  return null;
}
